// The platform release manifest — the Platform Update Framework's shared
// contract. Different framework phases diff it, read it, validate it, consume seed
// revs, and report against it, respectively. The authoritative machine schema is
// release-manifest.schema.json (kept in sync by release-manifest.test.ts).
// validateManifest() is a dependency-free structural check for runtime use.

export type Severity = "critical" | "recommended" | "optional";

export interface ImageComponent {
  kind: "image";
  ref: string;
  tag: string;
  digest: string; // sha256:…
  version: string;
  role?: string; // e.g. "converge-engine" for the installer
}

export interface NpmComponent {
  kind: "npm";
  package: string;
  version: string;
  min: string;
  recommended: string;
}

/**
 * Where a content item's bytes came from, when they did NOT come from the platform
 * repo's own tree.
 *
 * Present on items sourced from a standalone agent repo. Its presence is what
 * distinguishes the two provenances: an agent-sourced item's `treeSha` is a content
 * digest verified at hydration, so checking it against the platform repo's git tree
 * is meaningless — and silently skipping that check for EVERY item, which is what a
 * bare "not found in the tree" degradation does, is how an unverified persona would
 * ship looking verified.
 */
export interface ContentSource {
  /** `owner/name` of the agent repo. */
  repo: string;
  /** The 40-hex commit the content was taken from. */
  ref: string;
}

export interface PersonaItem {
  path: string;
  treeSha: string;
  version?: string | null;
  source?: ContentSource;
}

export interface BrainSeedItem {
  path: string;
  subtrees: Record<string, string | null>;
  source?: ContentSource;
}

export interface PersonaSet {
  kind: "content-set";
  basePath: "personas";
  items: Record<string, PersonaItem>;
}

export interface BrainSeedSet {
  kind: "content-set";
  basePath: "brain-seeds";
  seedPaths: string[];
  items: Record<string, BrainSeedItem>;
}

export interface InfraComponent {
  kind: "infra";
  treeSha: string;
}

export type CompanionTarget = "darwin-arm64" | "darwin-x64" | "win32-arm64" | "win32-x64";

export interface CompanionAsset {
  /** Asset name on the companion release, e.g. companion-darwin-arm64.tar */
  asset: string;
  /** Of the asset's own bytes — checked before anything is unpacked from it. */
  sha256: string;
}

/**
 * The desktop companions. Versioned and released on their own, and pinned here
 * the same way the images are: this names a build, it does not contain one.
 *
 * Optional, so a manifest cut before the companions existed still parses.
 */
export interface CompanionComponent {
  kind: "desktop";
  version: string;
  /** Release tag carrying the assets, e.g. companion-v0.1.0 */
  tag: string;
  targets: Partial<Record<CompanionTarget, CompanionAsset>>;
}

export interface PlatformMeta {
  version: string;
  tag: string;
  severity: Severity;
  releasedAt: string;
  commit: string;
  previousVersion: string | null;
  notes: string;
  notesUrl: string;
}

export interface ReleaseManifest {
  schemaVersion: 1;
  platform: PlatformMeta;
  components: {
    gateway: ImageComponent;
    codingAgent: ImageComponent;
    azureExecutor: ImageComponent;
    installer: ImageComponent;
    cli: NpmComponent;
    personas: PersonaSet;
    brainSeeds: BrainSeedSet;
    infra?: InfraComponent;
    companion?: CompanionComponent;
    /**
     * The agent repos this release consumed, verbatim from the release pin.
     *
     * The update rail hydrates from HERE rather than fetching a pin file
     * separately, so the manifest stays the release's own complete record of what
     * it deploys. Absent on releases cut before agent repos existed — which read
     * as "no agent-sourced content", exactly what those releases had.
     */
    agents?: Record<string, AgentPin>;
  };
  signature: unknown;
}

/** One pinned agent repo. Mirrors installer/release-pin.json's `agents.<key>`. */
export interface AgentPin {
  repo: string;
  /** A 40-hex commit. Never a tag or branch: a pin that can move is not a pin. */
  ref: string;
  /** `private` makes a read token mandatory when fetching. */
  visibility: "public" | "private";
  /** The persona slug this repo provides. */
  persona: string;
  /** `sha256:<hex>` over the consumed set; verified by value at hydration. */
  contentSha: string;
}

const SEVERITIES: Severity[] = ["critical", "recommended", "optional"];
const IMAGE_KEYS = ["gateway", "codingAgent", "azureExecutor", "installer"] as const;
export const COMPANION_TARGETS: readonly CompanionTarget[] = [
  "darwin-arm64",
  "darwin-x64",
  "win32-arm64",
  "win32-x64",
];

/** The target a companion build must match to run on this machine. */
export function companionTargetFor(
  platform: string,
  architecture: string,
): CompanionTarget | null {
  const target = `${platform}-${architecture}`;
  return COMPANION_TARGETS.includes(target as CompanionTarget) ? (target as CompanionTarget) : null;
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Dependency-free structural validation. Returns [] when valid, else error strings. */
export function validateManifest(m: unknown): string[] {
  const errors: string[] = [];
  if (!isObj(m)) return ["manifest is not an object"];

  if (m.schemaVersion !== 1) errors.push("schemaVersion must be the integer 1");

  const p = m.platform;
  if (!isObj(p)) {
    errors.push("platform is required");
  } else {
    for (const f of ["version", "tag", "releasedAt", "commit", "notes", "notesUrl"] as const) {
      if (typeof p[f] !== "string" || p[f].length === 0) errors.push(`platform.${f} is required`);
    }
    if (!SEVERITIES.includes(p.severity as Severity)) errors.push(`platform.severity must be one of ${SEVERITIES.join("|")}`);
    if (!(typeof p.previousVersion === "string" || p.previousVersion === null)) {
      errors.push("platform.previousVersion must be a string or null");
    }
  }

  const c = m.components;
  if (!isObj(c)) {
    errors.push("components is required");
    return errors;
  }
  for (const key of IMAGE_KEYS) {
    const img = c[key];
    if (!isObj(img)) {
      errors.push(`components.${key} is required`);
      continue;
    }
    if (img.kind !== "image") errors.push(`components.${key}.kind must be "image"`);
    for (const f of ["ref", "tag", "version"] as const) {
      if (typeof img[f] !== "string" || img[f].length === 0) errors.push(`components.${key}.${f} is required`);
    }
    if (typeof img.digest !== "string" || !/^sha256:[0-9a-f]+$/.test(img.digest)) {
      errors.push(`components.${key}.digest must be a sha256:… digest`);
    }
  }
  const cli = c.cli;
  if (!isObj(cli) || cli.kind !== "npm") {
    errors.push("components.cli (kind: npm) is required");
  } else {
    for (const f of ["package", "version", "min", "recommended"] as const) {
      if (typeof cli[f] !== "string" || cli[f].length === 0) errors.push(`components.cli.${f} is required`);
    }
  }
  const personas = c.personas;
  if (!isObj(personas) || !isObj(personas.items)) {
    errors.push("components.personas.items is required");
  } else {
    for (const [name, item] of Object.entries(personas.items)) {
      if (!isObj(item) || typeof item.path !== "string" || item.path.length === 0) {
        errors.push(`components.personas.items.${name}.path is required`);
      }
      if (!isObj(item) || typeof item.treeSha !== "string" || item.treeSha.length < 4) {
        errors.push(`components.personas.items.${name}.treeSha is required`);
      }
    }
  }
  const seeds = c.brainSeeds;
  if (!isObj(seeds) || !isObj(seeds.items)) {
    errors.push("components.brainSeeds.items is required");
  } else {
    for (const [name, item] of Object.entries(seeds.items)) {
      if (!isObj(item) || typeof item.path !== "string" || item.path.length === 0) {
        errors.push(`components.brainSeeds.items.${name}.path is required`);
      }
      if (!isObj(item) || !isObj(item.subtrees)) {
        errors.push(`components.brainSeeds.items.${name}.subtrees is required`);
      }
    }
  }
  const infra = c.infra;
  if (infra !== undefined) {
    if (!isObj(infra) || infra.kind !== "infra") {
      errors.push('components.infra.kind must be "infra"');
    }
    if (!isObj(infra) || typeof infra.treeSha !== "string" || infra.treeSha.length < 4) {
      errors.push("components.infra.treeSha is required");
    }
  }
  // Absent from every manifest cut before the companions were released, and a
  // reader older than this field ignores it — so it is checked only when a cut
  // claims to carry one.
  const companion = c.companion;
  if (companion !== undefined) {
    if (!isObj(companion) || companion.kind !== "desktop") {
      errors.push('components.companion.kind must be "desktop"');
    } else {
      for (const f of ["version", "tag"] as const) {
        if (typeof companion[f] !== "string" || companion[f].length === 0) {
          errors.push(`components.companion.${f} is required`);
        }
      }
      if (!isObj(companion.targets) || Object.keys(companion.targets).length === 0) {
        errors.push("components.companion.targets must name at least one target");
      } else {
        for (const [name, target] of Object.entries(companion.targets)) {
          if (!COMPANION_TARGETS.includes(name as CompanionTarget)) {
            errors.push(`components.companion.targets.${name} is not a known target`);
            continue;
          }
          if (!isObj(target) || typeof target.asset !== "string" || target.asset.length === 0) {
            errors.push(`components.companion.targets.${name}.asset is required`);
          }
          if (!isObj(target) || typeof target.sha256 !== "string" || !/^[0-9a-f]{64}$/.test(target.sha256)) {
            errors.push(`components.companion.targets.${name}.sha256 must be a sha256 hex digest`);
          }
        }
      }
    }
  }
  return errors;
}
